import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { IconArrowDown, IconArrowUp } from '@/icons'
import { Button } from './Button'
import { EmptyState, ErrorState, TableSkeleton } from './States'

/**
 * The one table.
 *
 * Every list in this app is sorted, filtered and paginated by the *server*, so there is no row
 * model to compute here — a headless table library would ship its machinery unused. This renders
 * columns and rows, announces its sort state, and asks for the next page.
 */

export interface Column<Row> {
  readonly id: string
  readonly header: ReactNode
  readonly cell: (row: Row) => ReactNode
  /** The value the server sorts by. Omit for a column that cannot be sorted. */
  readonly sortKey?: string
  readonly align?: 'start' | 'end'
  readonly width?: string
  /** Hidden below `lg`, for a column that is useful but not essential on a narrow screen. */
  readonly isSecondary?: boolean
}

export interface SortState {
  readonly key: string
  readonly order: 'asc' | 'desc'
}

export interface DataTableProps<Row> {
  readonly columns: readonly Column<Row>[]
  readonly rows: readonly Row[]
  readonly getRowId: (row: Row) => string
  readonly caption: string

  readonly isLoading?: boolean
  readonly error?: unknown
  readonly onRetry?: () => void

  readonly sort?: SortState | undefined
  readonly onSortChange?: (sort: SortState) => void

  readonly onRowClick?: (row: Row) => void
  readonly emptyTitle: string
  /* `| undefined` is required by exactOptionalPropertyTypes: callers pass a
     conditional expression that may evaluate to undefined. */
  readonly emptyDescription?: string | undefined
  readonly emptyAction?: ReactNode

  readonly hasNextPage?: boolean
  readonly isFetchingNextPage?: boolean
  readonly onLoadMore?: () => void
}

export function DataTable<Row>({
  columns,
  rows,
  getRowId,
  caption,
  isLoading = false,
  error,
  onRetry,
  sort,
  onSortChange,
  onRowClick,
  emptyTitle,
  emptyDescription,
  emptyAction,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
}: DataTableProps<Row>) {
  const { t } = useTranslation()

  if (error) return <ErrorState error={error} {...(onRetry ? { onRetry } : {})} />
  if (isLoading) return <TableSkeleton columns={columns.length} />

  if (rows.length === 0) {
    return (
      <EmptyState
        title={emptyTitle}
        {...(emptyDescription ? { description: emptyDescription } : {})}
        {...(emptyAction ? { action: emptyAction } : {})}
      />
    )
  }

  return (
    <div>
      {/* Wide tables scroll inside this box; the page body never scrolls sideways. */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="border-b border-border">
              {columns.map((column) => {
                // Narrowed once here rather than with `sort?.` at each use: inside the
                // callbacks below TypeScript cannot see that the optional chain already held.
                const activeSort = sort && sort.key === column.sortKey ? sort : null
                const canSort = Boolean(column.sortKey && onSortChange)

                return (
                  <th
                    key={column.id}
                    scope="col"
                    style={column.width ? { width: column.width } : undefined}
                    /*
                     * aria-sort belongs on the header cell, not the button. A screen reader
                     * announces the column's state when it lands on the cell.
                     */
                    aria-sort={
                      activeSort ? (activeSort.order === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                    className={cn(
                      'px-4 py-2.5 text-xs leading-4 font-medium tracking-wide text-text-secondary uppercase',
                      column.align === 'end' && 'text-right',
                      column.isSecondary && 'hidden lg:table-cell',
                    )}
                  >
                    {canSort && column.sortKey ? (
                      <button
                        type="button"
                        onClick={() => {
                          onSortChange?.({
                            key: column.sortKey ?? '',
                            // Clicking the sorted column flips it; a new column starts descending,
                            // because "most recent" and "largest" are what people want first.
                            order: activeSort?.order === 'desc' ? 'asc' : 'desc',
                          })
                        }}
                        className={cn(
                          'group inline-flex items-center gap-1 rounded transition-colors hover:text-text',
                          column.align === 'end' && 'flex-row-reverse',
                          activeSort && 'text-text',
                        )}
                      >
                        {column.header}
                        <span
                          aria-hidden
                          className={cn(
                            'transition-opacity',
                            activeSort ? 'opacity-100' : 'opacity-0 group-hover:opacity-40',
                          )}
                        >
                          {activeSort?.order === 'asc' ? (
                            <IconArrowUp size={12} />
                          ) : (
                            <IconArrowDown size={12} />
                          )}
                        </span>
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>

          <tbody className="divide-y divide-border">
            {rows.map((row) => (
              <tr
                key={getRowId(row)}
                onClick={onRowClick ? () => { onRowClick(row) } : undefined}
                className={cn(
                  'transition-colors',
                  onRowClick && 'cursor-pointer hover:bg-surface-sunken',
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={cn(
                      'px-4 py-2.5 text-[0.8125rem] leading-5 text-text',
                      column.align === 'end' && 'text-right',
                      column.isSecondary && 'hidden lg:table-cell',
                    )}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasNextPage && onLoadMore ? (
        <div className="flex justify-center border-t border-border p-3">
          <Button variant="secondary" size="sm" onClick={onLoadMore} isLoading={isFetchingNextPage}>
            {t('common.loadMore')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
